'use es6';

import { memo, useCallback, useEffect } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import classNames from 'classnames';
import { EERIE, CALYPSO_LIGHT, CANDY_APPLE } from 'HubStyleTokens/colors';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import PhoneNumberWithProperty from './PhoneNumberWithProperty';
import { getValue, getPropertyName, getObjectId, getMetadata, getValidNumber } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { logCallingError } from 'calling-error-reporting/report/error';
import { PhoneNumberIdentifier } from 'calling-lifecycle-internal/callees/records/PhoneNumberIdentifier';
import { getObjectTypeId } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import UIButton from 'UIComponents/button/UIButton';
import { useAccessLevel } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { MOVE_INBOX_TO_CALLTAB } from 'calling-internal-common/constants/CallingGates';
import { HELP_DESK, INBOX } from 'calling-internal-common/constants/appIdentifiers';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledPhoneNumberButton = styled(UIButton).withConfig({
  displayName: "PhoneNumberOption__StyledPhoneNumberButton",
  componentId: "sc-1mpwhxv-0"
})(["cursor:pointer;width:100%;background-color:transparent;&.is--disabled{opacity:0.5;}&:hover{background-color:", ";}"], CALYPSO_LIGHT);
export function PhoneNumberOption({
  appIdentifier,
  className,
  onSelect,
  onEditProperty,
  phoneNumberProperty,
  callableObject,
  editPermissions,
  disabled,
  OptionIcon,
  disabledTooltipMessage,
  formattedName,
  objectTypeId,
  hidePropertyName,
  gates,
  selectToNumberIndex
}) {
  const phoneNumberValue = getValue(phoneNumberProperty);
  const propertyName = getPropertyName(phoneNumberProperty);
  const phoneNumberMetadata = getMetadata(phoneNumberProperty);
  const isValidNumber = getValidNumber(phoneNumberMetadata);
  const isHelpdesk = appIdentifier === HELP_DESK;
  const isInbox = appIdentifier === INBOX;
  const inboxIsUngatedForCallTab = gates && gates.includes(MOVE_INBOX_TO_CALLTAB);
  const showInvalidNumber = (isHelpdesk || isInbox && inboxIsUngatedForCallTab) && !isValidNumber;
  const accessLevel = useAccessLevel({
    objectType: objectTypeId,
    propertyName
  });
  useEffect(() => {
    if (!phoneNumberValue) {
      logCallingError({
        errorMessage: 'PhoneNumberOption displayed a number without a value',
        extraData: {
          phoneNumberProperty,
          callableObject
        }
      });
    }
  }, [callableObject, phoneNumberProperty, phoneNumberValue]);
  let isLoadingPermissions = false;
  let canEdit = true;
  let tooltipKey = 'noEditPermissions';
  if (editPermissions) {
    isLoadingPermissions = editPermissions.get('isLoading');
    canEdit = editPermissions.get('canEdit');
    tooltipKey = isLoadingPermissions ? 'loadingPermissions' : tooltipKey;
  }
  const handleSelect = useCallback(() => {
    if (!disabled && !showInvalidNumber && phoneNumberValue) {
      const numberIdentifier = new PhoneNumberIdentifier({
        objectTypeId: getObjectTypeId(callableObject),
        objectId: getObjectId(callableObject),
        propertyName
      });
      onSelect({
        numberIdentifier,
        callableObject
      });
      if (selectToNumberIndex > -1) {
        const action = selectToNumberIndex === 0 ? 'call the first number' : 'call secondary numbers';
        CommunicatorLogger.log('Calling_DropDownInteractions', {
          action,
          source: appIdentifier,
          selectToNumberPosition: selectToNumberIndex + 1
        });
      }
    }
  }, [disabled, showInvalidNumber, phoneNumberValue, callableObject, propertyName, onSelect, selectToNumberIndex, appIdentifier]);
  const handleEditOption = useCallback(evt => {
    evt.stopPropagation();
    onEditProperty(callableObject, phoneNumberProperty);
  }, [callableObject, onEditProperty, phoneNumberProperty]);
  const classes = classNames('calling-phone-option align-center p-y-2 p-x-3', className, (disabled || showInvalidNumber) && 'is--disabled');
  if (accessLevel === HIDDEN) {
    return null;
  }
  return /*#__PURE__*/_jsx(UITooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: disabledTooltipMessage || 'callee-selection.invalidNumber'
    }),
    "data-selenium-test": "hs-provided-and-call-from-phone-tooltip",
    placement: "bottom",
    disabled: !disabledTooltipMessage && !showInvalidNumber,
    children: /*#__PURE__*/_jsxs(StyledPhoneNumberButton, {
      use: "unstyled",
      className: classes,
      onClick: handleSelect,
      "data-number-property": propertyName,
      "data-object-id": getObjectId(callableObject),
      "data-raw-number": phoneNumberValue,
      "aria-label": `Call ${formattedName}`,
      children: [/*#__PURE__*/_jsxs("div", {
        style: {
          flexGrow: 1
        },
        children: [OptionIcon && /*#__PURE__*/_jsx(OptionIcon, {}), /*#__PURE__*/_jsx(PhoneNumberWithProperty, {
          phoneNumberProperty: phoneNumberProperty,
          hidePropertyName: hidePropertyName
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-left-2",
        children: [showInvalidNumber && /*#__PURE__*/_jsx(UIIcon, {
          name: "warning",
          size: "xxs",
          color: CANDY_APPLE,
          "data-selenium-test": "invalid-number-icon"
        }), onEditProperty && /*#__PURE__*/_jsx(UITooltip, {
          disabled: canEdit,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `callee-selection.phoneNumberErrors.${tooltipKey}`
          }),
          children: /*#__PURE__*/_jsx(UIIconButton, {
            style: {
              zIndex: 100
            },
            use: "unstyled",
            "data-selenium-test": "edit-number-button",
            onClick: handleEditOption,
            disabled: !canEdit || isLoadingPermissions,
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "edit",
              size: "xxs",
              color: EERIE
            })
          })
        })]
      })]
    })
  });
}
PhoneNumberOption.propTypes = {
  appIdentifier: PropTypes.string.isRequired,
  className: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onEditProperty: PropTypes.func,
  editPermissions: ImmutablePropTypes.map,
  disabled: PropTypes.bool,
  OptionIcon: PropTypes.object,
  phoneNumberProperty: RecordPropType('PhoneNumberProperty').isRequired,
  callableObject: RecordPropType('CallableObject').isRequired,
  disabledTooltipMessage: PropTypes.string,
  formattedName: PropTypes.string.isRequired,
  objectTypeId: PropTypes.string.isRequired,
  hidePropertyName: PropTypes.bool,
  gates: PropTypes.arrayOf(PropTypes.string),
  selectToNumberIndex: PropTypes.number
};
PhoneNumberOption.defaultProps = {
  editPermissions: null,
  disabled: false,
  OptionIcon: null
};
export default /*#__PURE__*/memo(PhoneNumberOption);